import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import {
    collection,
    addDoc,
    query,
    where,
    orderBy,
    getDocs,
    serverTimestamp,
    updateDoc,
    doc,
    arrayUnion,
    arrayRemove,
    deleteDoc,
    getDoc,
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { useParams, Link } from "react-router-dom";

const anthemsMap = {
    anthem1: "/audio/anthem1.mp3",
    anthem2: "/audio/anthem2.mp3",
    anthem3: "/audio/anthem3.mp3",
    anthem4: "/audio/anthem4.mp3",
    anthem5: "/audio/anthem5.mp3",
    anthem6: "/audio/anthem6.mp3",
};
const Wall = () => {
    const { currentUser } = useAuth();
    const { userId } = useParams();
    const [isFriend, setIsFriend] = useState(false);
    const [showAddButton, setShowAddButton] = useState(false);
    const isMyWall = !userId || userId === currentUser?.uid;
    const [message, setMessage] = useState("");
    const [postText, setPostText] = useState("");
    const [posts, setPosts] = useState([]);
    const [userProfile, setUserProfile] = useState(null);
    const [anthemSrc, setAnthemSrc] = useState(null);
    const [commentsMap, setCommentsMap] = useState({});
    const [authorsCache, setAuthorsCache] = useState({}); // кэш авторов постов
    const [friends, setFriends] = useState([]);
    const audioRef = useRef(null);

    // Загружаем профиль владельца стены
    useEffect(() => {

        async function loadComments(postId) {
            try {
                const commentsSnapshot = await getDocs(
                    query(collection(db, "comments"), where("postId", "==", postId))
                );

                const comments = commentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // Обновим комментарии в состоянии
                setCommentsMap(prev => ({ ...prev, [postId]: comments }));

                // 👉 Загрузка авторов комментариев
                comments.forEach(async (comment) => {
                    const authorId = comment.authorId;

                    // Если этого автора ещё нет в кэше
                    if (!authorsCache[authorId]) {
                        const userDoc = await getDoc(doc(db, "users", authorId));
                        if (userDoc.exists()) {
                            const authorData = userDoc.data();
                            setAuthorsCache(prev => ({
                                ...prev,
                                [authorId]: {
                                    displayName: authorData.displayName || "Без имени",
                                    avatarUrl: authorData.avatarUrl || "",
                                },
                            }));
                        }
                    }
                });
            } catch (error) {
                console.error("Ошибка при загрузке комментариев:", error);
            }
        }

        const fetchUserProfile = async () => {
            if (!userId && !currentUser) return;

            const profileUserId = userId || currentUser.uid;
            const userRef = doc(db, "users", profileUserId);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
                const data = userSnap.data();
                setUserProfile(data);
                setAnthemSrc(anthemsMap[data.anthem] || null);
            } else if (profileUserId === currentUser.uid) {
                setUserProfile({
                    displayName: currentUser.email,
                    avatarUrl: "",
                });
                setAnthemSrc(null);
            } else {
                setUserProfile({
                    displayName: "Неизвестный пользователь",
                    avatarUrl: "",
                });
                setAnthemSrc(null);
            }

        };
        const fetchFriends = async () => {
            if (!userId && !currentUser) return;

            const profileUserId = userId || currentUser.uid;
            const friendsRef = collection(db, "users", profileUserId, "friends");
            const snapshot = await getDocs(friendsRef);

            // Загружаем данные каждого друга
            const list = await Promise.all(
                snapshot.docs.map(async (docSnap) => {
                    const friendId = docSnap.id;
                    const userRef = doc(db, "users", friendId);
                    const userSnap = await getDoc(userRef);
                    const userData = userSnap.exists() ? userSnap.data() : {};

                    return {
                        id: friendId,
                        ...userData,
                    };
                })
            );

            setFriends(list);
        };

        fetchUserProfile();
        const checkFriendStatus = async () => {
            if (!currentUser || !userId || userId === currentUser.uid) {
                setShowAddButton(false);
                return;
            }

            const friendDocRef = doc(db, "users", currentUser.uid, "friends", userId);
            const friendDocSnap = await getDoc(friendDocRef);

            const isAlreadyFriend = friendDocSnap.exists();
            setIsFriend(isAlreadyFriend);
            setShowAddButton(!isAlreadyFriend);
        };

        checkFriendStatus();
        fetchFriends();
    }, [userId, currentUser]);

    const handleAddComment = async (postId, commentText) => {
        if (!commentText.trim()) return;

        try {
            await addDoc(collection(db, "posts", postId, "comments"), {
                text: commentText.trim(),
                authorId: currentUser.uid,
                createdAt: serverTimestamp(),
            });

            fetchComments(postId); // обновим комментарии
        } catch (error) {
            console.error("Ошибка при добавлении комментария:", error);
        }
    };

    const fetchComments = async (postId) => {
        const commentsRef = collection(db, "posts", postId, "comments");
        const snapshot = await getDocs(commentsRef);

        const commentsWithAuthors = await Promise.all(
            snapshot.docs.map(async (docSnap) => {
                const comment = docSnap.data();
                const authorRef = doc(db, "users", comment.authorId);
                const authorSnap = await getDoc(authorRef);

                const authorData = authorSnap.exists() ? authorSnap.data() : {
                    displayName: "Неизвестный",
                    avatarUrl: null,
                };

                return {
                    id: docSnap.id,
                    ...comment,
                    author: {
                        id: comment.authorId,
                        displayName: authorData.displayName || "Без имени",
                        avatarUrl: authorData.avatarUrl || authorData.photoURL || null,
                    },
                };
            })
        );

        setCommentsMap(prev => ({
            ...prev,
            [postId]: commentsWithAuthors
        }));
    };

    const handleDeleteComment = async (postId, commentId) => {
        try {
            const commentRef = doc(db, "posts", postId, "comments", commentId);
            await deleteDoc(commentRef);
            fetchComments(postId); // обновим список
        } catch (error) {
            console.error("Ошибка при удалении комментария:", error);
        }
    };


    const handleAddFriend = async () => {
        if (!currentUser || !userId || currentUser.uid === userId) return;

        try {
            const currentUserRef = doc(db, "users", currentUser.uid);
            const otherUserRef = doc(db, "users", userId);

            await updateDoc(currentUserRef, {
                friends: arrayUnion(userId)
            });

            await updateDoc(otherUserRef, {
                friends: arrayUnion(currentUser.uid)
            });

            setIsFriend(true);
            alert("Пользователь добавлен в друзья!");
        } catch (error) {
            console.error("Ошибка при добавлении в друзья:", error);
        }
    };


    // Загрузка постов и подгрузка профилей авторов
    const fetchPosts = async () => {
        const profileUserId = userId || currentUser?.uid;
        if (!profileUserId) return;

        const q = query(
            collection(db, "posts"),
            where("userId", "==", profileUserId),
            where("type", "==", "wall"),
            orderBy("createdAt", "desc")
        );

        const snapshot = await getDocs(q);
        const postsArray = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                likes: data.likes || [],
                createdAt: data.createdAt || null,
            };
        });

        setPosts(postsArray);

        // Загружаем комментарии для всех постов
        await Promise.all(postsArray.map(post => fetchComments(post.id)));

        // Загружаем данные авторов постов
        const uniqueAuthorIds = [...new Set(postsArray.map(p => p.authorId || p.userId))];
        const newAuthorsCache = { ...authorsCache };

        await Promise.all(uniqueAuthorIds.map(async (authorId) => {
            if (!newAuthorsCache[authorId]) {
                const authorRef = doc(db, "users", authorId);
                const authorSnap = await getDoc(authorRef);
                newAuthorsCache[authorId] = authorSnap.exists()
                    ? authorSnap.data()
                    : { displayName: "Неизвестный", avatarUrl: "" };
            }
        }));

        setAuthorsCache(newAuthorsCache);
    };


    useEffect(() => {
        fetchPosts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, currentUser]);
    useEffect(() => {
        const audio = audioRef.current;

        if (audio) {
            audio.pause();
            audio.currentTime = 0;
        }

        if (anthemSrc && audio) {
            audio.src = anthemSrc;
            audio.play().catch((e) => {
                console.warn("Автопроигрывание заблокировано:", e);
            });
        }

        return () => {
            if (audio) {
                audio.pause();
                audio.currentTime = 0;
            }
        };
    }, [anthemSrc]);


    const handleDelete = async (postId) => {
        const postRef = doc(db, "posts", postId);
        try {
            await deleteDoc(postRef);
            fetchPosts();
        } catch (error) {
            console.error("Ошибка при удалении поста:", error);
        }
    };

    const handlePost = async () => {
        if (!postText.trim()) return;

        try {
            await addDoc(collection(db, "posts"), {
                userId: userId || currentUser.uid, // на чьей стене
                authorId: currentUser.uid,         // кто пишет
                email: currentUser.email,
                text: postText.trim(),
                createdAt: serverTimestamp(),
                type: "wall",
            });

            setPostText("");
            fetchPosts();
        } catch (error) {
            console.error("Ошибка при публикации поста:", error);
        }
    };

    const toggleLike = async (postId, likes) => {
        const postRef = doc(db, "posts", postId);
        const isLiked = likes.includes(currentUser.uid);

        try {
            await updateDoc(postRef, {
                likes: isLiked
                    ? arrayRemove(currentUser.uid)
                    : arrayUnion(currentUser.uid),
            });
            fetchPosts();
        } catch (error) {
            console.error("Ошибка при обновлении лайка:", error);
        }
    };

    const CommentInput = ({ postId }) => {
        const [text, setText] = useState("");

        const submit = () => {
            handleAddComment(postId, text);
            setText("");
        };

        return (
            <div style={{ marginTop: "16px", display: "flex", alignItems: "center" }}>
                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Ваш комментарий..."
                    style={{
                        background: "rgba(0, 0, 0, 0.7)",
                        border: "2px solid var(--neon-cyan)",
                        borderRadius: "6px",
                        padding: "10px 15px",
                        width: "85%",
                        color: "white",
                        fontSize: "16px",
                        boxShadow: "0 0 10px rgba(0, 238, 255, 0.3)",
                        resize: "none",
                        minHeight: "50px",
                        transition: "all 0.3s ease",
                        outline: "none"
                    }}
                />
                <button onClick={submit} style={{ marginLeft: "8px" }}>Отправить</button>
            </div>
        );
    };


    const handleSendRequest = async (toUserId) => {
        try {
            // Создаём заявку в коллекции friendRequests
            await addDoc(collection(db, "friendRequests"), {
                fromUserId: currentUser.uid,
                toUserId,
                status: "pending",
                createdAt: serverTimestamp(),
            });

            setMessage("Заявка отправлена");
        } catch (error) {
            setMessage("Ошибка при отправке заявки: " + error.message);
        }
    };
    return (
        <div style={{ maxWidth: "1000px", margin: "0 auto" }} className="background-personalWall">
            <h2 className="wall-header">
                {isMyWall ? "Моя стена" : `Стена пользователя ${userProfile?.displayName || ""}`}
            </h2>
            <audio ref={audioRef} style={{ display: "none" }} />
            {showAddButton && (
                <button
                    onClick={() => handleSendRequest(userId)}

                    style={{ marginLeft: 10, padding: "4px 8px", cursor: "pointer" }}
                >
                    Добавить в друзья
                </button>

            )}
            {message && <p>{message}</p>}
            {/* Показываем поле для поста и кнопку всегда, если пользователь залогинен */}
            {isMyWall && currentUser && (
                <>
                    <textarea
                        value={postText}
                        onChange={(e) => setPostText(e.target.value)}
                        placeholder="Написать сообщение..."
                    />
                    <button onClick={handlePost}>
                        Опубликовать
                    </button>
                </>
            )}

            <div style={{ marginTop: "30px" }}>
                {posts.length === 0 && <p>Постов пока нет.</p>}
                {posts.map((post) => {

                    const author = authorsCache[post.authorId] || authorsCache[post.userId] || { displayName: "Неизвестный", avatarUrl: "" };
                    const canDelete = currentUser.uid === post.authorId;

                    return (
                        <div
                            key={post.id}
                            style={{
                                marginBottom: "20px",
                                padding: "15px",
                                border: "3px solid #444",
                                borderRadius: "8px",
                                position: "relative", // Добавляем для позиционирования
                            }}
                        >
                            {/* Верхняя часть поста (аватар + никнейм) */}
                            <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                                <Link to={`/wall/${post.authorId}`}>
                                    {author.avatarUrl && (
                                        <img
                                            src={author.avatarUrl}
                                            alt="аватар"
                                            style={{
                                                width: "50px",
                                                height: "50px",
                                                borderRadius: "50%",
                                                marginRight: "10px",
                                            }}
                                        />
                                    )}
                                </Link>
                                <Link to={`/wall/${post.authorId}`} className="nickname">
                                    {author.displayName}
                                </Link>

                            </div>

                            {/* Текст поста */}
                            <div className="textPost">{post.text}</div>

                            {/* Нижняя часть (дата + кнопки) */}
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-end",
                                marginTop: "15px"
                            }}>
                                {/* Кнопка лайка */}
                                <button
                                    onClick={() => toggleLike(post.id, post.likes)}
                                    className="like-button"
                                    style={{ margin: "0" }} // Убираем внешние отступы
                                >
                                    ❤️ {post.likes.length}
                                </button>

                                {/* Правая часть (дата + кнопка удаления) */}
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <div className="dateText" style={{ marginRight: "15px" }}>
                                        {post.createdAt?.toDate?.().toLocaleString() || "только что"}
                                    </div>

                                    {canDelete && (
                                        <button
                                            onClick={() => handleDelete(post.id)}
                                            className="delete-button"
                                            style={{ margin: "0" }}
                                        >
                                            🗑️ Удалить
                                        </button>
                                    )}
                                </div>
                            </div>
                            {/* Комментарии */}
                            <div style={{ marginTop: "15px", paddingLeft: "15px", borderLeft: "2px solid #666" }}>
                                <h4 style={{ marginBottom: "8px", color: "#ccc" }}>💬 Комментарии:</h4>
                                {(commentsMap[post.id] || []).map(comment => {
                                    const commentAuthor = comment.author || {
                                        displayName: "Неизвестный",
                                        id: comment.authorId
                                    };

                                    // 👇 Проверка права на удаление для КАЖДОГО комментария
                                    const canDelete =
                                        currentUser?.uid === comment.authorId || currentUser?.uid === post.userId;

                                    return (
                                        <div key={comment.id} style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                                            {/* Аватар + ник */}
                                            <div style={{ display: "flex", alignItems: "center" }}>
                                                <Link to={`/wall/${comment.authorId}`}>
                                                    {commentAuthor.avatarUrl && (
                                                        <img
                                                            src={commentAuthor.avatarUrl}
                                                            alt="аватар"
                                                            style={{
                                                                width: "40px",
                                                                height: "40px",
                                                                borderRadius: "50%",
                                                                marginRight: "10px",
                                                                objectFit: "cover",
                                                            }}
                                                        />
                                                    )}
                                                </Link>
                                                <Link to={`/wall/${comment.authorId}`} className="nickname">
                                                    {commentAuthor.displayName}
                                                </Link>
                                            </div>

                                            {/* Текст комментария */}
                                            <div className="textComments">{comment.text}</div>

                                            {/* Кнопка удалить, если пользователь автор комментария или поста */}
                                            {canDelete && (
                                                <button
                                                    onClick={() => handleDeleteComment(post.id, comment.id)}
                                                    style={{ marginLeft: "auto", color: "red", background: "transparent", border: "none", cursor: "pointer" }}
                                                >
                                                    Удалить
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}


                                <CommentInput postId={post.id} />
                            </div>

                        </div>
                    );
                })}

                {friends.length > 0 && (
                    <div style={{ marginTop: "40px" }}>
                        <h3 style={{ color: "white", fontSize: "1.2rem", marginBottom: "10px" }}>
                            👥 {isMyWall ? "Мои друзья" : `Друзья ${userProfile?.displayName || ""}`}
                        </h3>
                        {/* Скрытый аудиоэлемент для воспроизведения гимна */}
                        <audio ref={audioRef} style={{ display: "none" }} />
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
                            gap: "12px"
                        }}>
                            {friends.map(friend => (
                                <Link
                                    key={friend.id}
                                    to={`/wall/${friend.id}`}
                                    style={{
                                        background: "#2b2d42",
                                        borderRadius: "10px",
                                        padding: "8px",
                                        textAlign: "center",
                                        textDecoration: "none",
                                        color: "white"
                                    }}
                                >
                                    <div style={{
                                        width: "60px",
                                        height: "60px",
                                        borderRadius: "50%",
                                        overflow: "hidden",
                                        background: "#3a3b5a",
                                        margin: "0 auto 8px"
                                    }}>
                                        {friend.avatarUrl ? (
                                            <img
                                                src={friend.avatarUrl}
                                                alt="Аватар"
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />
                                        ) : (
                                            <span style={{
                                                display: "flex",
                                                width: "100%",
                                                height: "100%",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "20px"
                                            }}>
                                                {friend.displayName?.charAt(0) || "?"}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        fontSize: "0.8rem"
                                    }}>
                                        {friend.displayName || "Аноним"}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

            </div>
        </div>

    );
};

export default Wall;
